import type { ReactNode } from 'react';
import { MockPill } from '@components/ui/MockPill/MockPill';
import styles from './Panel.module.css';

type IPanelProps = {
  /** Usually a translated string; SiteMap composes it as a thumbnail + bold
   *  plan-name row instead of plain text. */
  title: ReactNode;
  subtitle?: string;
  headerRight?: ReactNode;
  /** Optional leading glyph shown before the title (e.g. a warning icon). */
  icon?: ReactNode;
  /** `warning` gives the panel an amber alert treatment. */
  tone?: 'default' | 'warning';
  /** Marks the panel's content as fixture data — renders a MOCK pill after the title. */
  mock?: boolean;
  /** Makes the body a flex column that fills the panel's height (for a panel
   *  stretched to a neighbour's height, so its own content can scroll to fit). */
  fill?: boolean;
  /** The page tour's handle for this panel — `data-tour` on the section. */
  tour?: string;
  children: ReactNode;
};

/** Shared panel chrome — titled card used by the logistics list panels. */
export function Panel({ title, subtitle, headerRight, icon, tone = 'default', mock = false, fill = false, tour, children }: IPanelProps) {
  return (
    <section className={styles.panel} data-tone={tone} data-tour={tour}>
      <header className={styles.head}>
        <div className={styles.heading}>
          {icon ? <span className={styles.icon}>{icon}</span> : null}
          <div>
            <h2 className={styles.title}>
              {title}
              {mock ? <MockPill kind="badge" /> : null}
            </h2>
            {subtitle ? <p className={styles.subtitle}>{subtitle}</p> : null}
          </div>
        </div>
        {headerRight ? <div className={styles.headerRight}>{headerRight}</div> : null}
      </header>
      <div className={`${styles.body}${fill ? ` ${styles.bodyFill}` : ''}`}>{children}</div>
    </section>
  );
}
